import { ApplicationModule, RootState, UserModule } from '../redux';
import { DispatchProp, connect } from 'react-redux';
import React, { PureComponent } from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import styled, { theme } from '../theme';

import { AccountSettingsContainer } from '../pages/account_settings';
import { AppHeader } from '../components/app_header/app_header';
import { CalculatorContainer } from '../components/calculator/calculator';
import { ClientsContainer } from '../pages/clients';
import { Overview } from '../pages/overview_shadcn';
import MyShopPage from '../pages/my-shop';
import { NotificationStackContainer } from '../components/notifications/notification_stack';
import QuipaHorizontal from '../assets/icons/quipa_horizontal.svg';
import { Sidebar } from '../components/sidebar/sidebar';
import { SuppliersContainer } from '../pages/suppliers';
import SupportModal from '../modals/support-modal';
import { UserSettingsContainer } from '../pages/user_settings';
import { ApplicationContext } from '../ApplicationContext';
import { userCanCreateNewOrder } from '../api/drapery-order';
import SubscriptionPlanModal from '../modals/subscription_plan_modal';
import { changeSubscriptionPlan, getAccount } from '../api/account';
import { getCustomerPlanInfo } from '../api/stripe';
import moment from 'moment';
import { trackView } from '../components/analytics/track_view';
import QuickBookConnect from '../pages/quickbooks_connect';
import { quickbooksCheckIsConnected } from '../api/quickbooks';
import ProjectsPage from '../pages/projects';
import ClickOutside from '../ui_components/clickOutside/ClickOutside';
import AllUsersPage from '../pages/all_users';
import TeamSettingsPage from '../pages/team_settings';
import { getUser, handleUpdateUser } from '../api/users';
import { Account } from '../interfaces/account';
import QuipaAssistantLauncher from '../components/projects/quipa-assistant-launcher';
import { openQuipaAssistant } from '../components/projects/quipa-assistant-events';
import PurchasedGoods from '../components/purchasedGoods/PurchasedGoods';
import { Toaster } from '@/components/ui/sonner';
import { checkGate } from '../statsig';
import {
  clearCachedPricingPermission,
  readCachedPricingPermission,
  writeCachedPricingPermission,
} from '../helper/pricingPermission';
import { AnyAction } from 'redux';
import OrderingAndReceiving from '../pages/OrderingAndReceiving';

interface MainApplicationContainerProps {
  uiSidebarFull: boolean;
}

export type ScrollToPosition = 'top' | 'bottom';

const TEAM_SETTINGS_ACCESS_CACHE_PREFIX = 'team-settings-access-';
const TEAM_SETTINGS_PLAN_ACCESS_CACHE_PREFIX = 'team-settings-plan-access-';
const PRICING_TABLE_PLANS_GATE = 'pricing_table_plans';

const clearTeamSettingsAccessCache = () => {
  try {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (
        key &&
        (key.startsWith(TEAM_SETTINGS_ACCESS_CACHE_PREFIX) ||
          key.startsWith(TEAM_SETTINGS_PLAN_ACCESS_CACHE_PREFIX))
      ) {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    // Ignore cache cleanup failures.
  }
};

const getTeamSettingsPlanAccessCacheKey = (accountId: number): string =>
  `${TEAM_SETTINGS_PLAN_ACCESS_CACHE_PREFIX}${accountId}`;

const readTeamSettingsPlanAccessCache = (accountId: number): boolean | null => {
  if (!accountId) {
    return null;
  }

  try {
    const raw = localStorage.getItem(getTeamSettingsPlanAccessCacheKey(accountId));
    if (raw === null) {
      return null;
    }
    return raw === '1';
  } catch (error) {
    return null;
  }
};

const writeTeamSettingsPlanAccessCache = (accountId: number, canAccess: boolean): void => {
  if (!accountId) {
    return;
  }

  try {
    localStorage.setItem(getTeamSettingsPlanAccessCacheKey(accountId), canAccess ? '1' : '0');
  } catch (error) {
    // Ignore cache write failures.
  }
};

const hasTeamSettingsSeatsMetadata = (planInfo: any): boolean => {
  const metadata = planInfo?.product_metadata;
  if (!metadata || typeof metadata !== 'object') {
    return false;
  }

  const seatsValue = metadata.seats;
  if (seatsValue === undefined || seatsValue === null || seatsValue === '') {
    return false;
  }

  const numericSeats = Number(seatsValue);
  return Number.isFinite(numericSeats) && numericSeats > 1;
};

const hasPricingAccess = (value: any): boolean => value === true || value === 1 || value === '1';
const ALLOWED_ESTIMATE_SETTINGS_ITEMS = new Set(['drapery-item', 'roman-shade-item']);

const hasRestrictedEstimateSettingsMyShopAccess = (
  location: RouteComponentProps['location']
): boolean => {
  if (!location || location.pathname !== '/myshop') {
    return false;
  }

  const searchParams = new URLSearchParams(location.search || '');
  const item = searchParams.get('item');
  const tab = searchParams.get('tab');

  return tab === 'estimate-settings' && !!item && ALLOWED_ESTIMATE_SETTINGS_ITEMS.has(item);
};

/**
 * General application container
 */
const MainApplicationContainer = styled.div<MainApplicationContainerProps>`
  height: 100%;
  width: 100%;
  margin: 0;
  display: grid;
  grid-template-columns: ${theme.elements.sidebarSmallSize}px 1fr;
  grid-template-rows: ${theme.elements.headerFullSize}px 1fr;
  grid-template-areas:
    'sidebar_control header'
    'sidebar main';
  grid-gap: 0 0;
  justify-items: stretch;
  align-items: stretch;
`;

const HeaderContent = styled.div`
  grid-area: header;
  background: ${theme.colors.primary};
  color: ${theme.getContrastTextColor(theme.colors.primary)};
  position: fixed;
  top: 0;
  left: 0;
  height: 65px;
  width: 100%;
  z-index: 999;
`;

const QuipaLogoContainer = styled.div`
  cursor: pointer;
`;

const QuipaLogo = styled(QuipaHorizontal)`
  float: left;
  margin-left: 11px;
  height: 65px;
`;

const MainContent = styled.main`
  grid-area: main;
  background: #fff;
  margin: 0;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: stretch;
  position: relative;
  height: auto;
  min-width: 0;
  overflow-x: hidden;
`;

const SidebarContent = styled.aside`
  grid-area: sidebar;
  background: ${theme.colors.primary};
  color: ${theme.getContrastTextColor(theme.colors.primary)};
  position: fixed;
  left: 0;
  top: 65px;
  height: 100%;
  z-index: 500;
`;

// tslint:disable-next-line:no-empty-interface
export interface ApplicationOwnProps extends RouteComponentProps {}

interface ApplicationProps extends ApplicationOwnProps {
  /**
   * Auth token
   */
  token?: string | null;
  /**
   * True when app was initially loaded
   */
  appLoaded: boolean;
  /**
   * User full name
   */
  userName: string;
  /**
   * User role
   */
  userRole: 'admin' | 'office_worker';
  /**
   * First name
   */
  firstName: string;
  /**
   * Last name
   */
  lastName: string;
  /**
   * AvatarId
   */
  avatarId?: string | null;
  /**
   * Account Id
   */
  accountId: number;
  /**
   * Email
   */
  userEmail: string;

  createdAt: string;

  logoId?: string | null;
}

interface State {
  /**
   * True when sidebar is fully expanded
   */
  fullSidebar: boolean;
  /**
   * Initial loading error
   */
  loadingError?: string;
  /**
   * Support Modal
   */
  isSupportModalOpen?: boolean;

  previousStatus?: string | null;

  isOpen: boolean;

  isOpenModal: boolean;

  cantLogOut: boolean;

  triggerModal: boolean;
  // shouldRenderOverview: boolean;
  /**
   * Subscription modal modal
   */
  subscriptionPlanModalOpen: boolean;
  /**
   * Stripe renewal date
   */
  stripeRenewalDate?: string;
  isConnected: boolean;
  accountIsAdmin: boolean;
  accountCanSeePricing: boolean;
  remainingItemOrder: number;
  bottomSheetActive: boolean;
  purchasedGoodsId: number | null;
  purchasedGoodsRefetch: boolean;
  prefilledClient: any;
  canAccessTeamSettings: boolean;
  accountUser: any;
  hasResolvedTeamSettingsAccess: boolean;
  refetchUser: boolean;
  refetchAccount: boolean;
  backToTop: boolean;
  purchasedGoodsPrefill: any;
  account: any;
}

export class Application extends PureComponent<ApplicationProps & DispatchProp, State> {
  static contextType = ApplicationContext;
  private static readonly PENDING_ACCOUNT_STATUS = 'pending';
  private static readonly UNSUBSCRIBED_ACCOUNT_STATUS = 'unsubscribed';
  public appRef = React.createRef<any>();

  public state: State = {
    fullSidebar: false,
    isOpen: false,
    isOpenModal: false,
    previousStatus: localStorage.getItem('status'),
    cantLogOut: false,
    triggerModal: false,
    subscriptionPlanModalOpen: false,
    stripeRenewalDate: undefined,
    isConnected: false,
    accountIsAdmin: false,
    accountCanSeePricing: readCachedPricingPermission(false),
    remainingItemOrder: 0,
    bottomSheetActive: false,
    purchasedGoodsId: null,
    purchasedGoodsRefetch: false,
    prefilledClient: null,
    canAccessTeamSettings: readTeamSettingsPlanAccessCache(this.props.accountId) === true,
    hasResolvedTeamSettingsAccess: readTeamSettingsPlanAccessCache(this.props.accountId) !== null,
    accountUser: null,
    refetchUser: false,
    refetchAccount: false,
    backToTop: false,
    purchasedGoodsPrefill: null,
    account: null,
    // shouldRenderOverview: false,
  };

  public async componentDidMount(): Promise<void> {
    const { dispatch, history } = this.props;
    // Bail if application already loaded
    const appLoaded = localStorage.getItem('appLoaded');
    const { user, setUser } = this.context;
    const token = user.user.token || '';
    if (token) {
      const jwtPayload = JSON.parse(atob(token.split('.')[1]));
      const { exp, accountId } = jwtPayload;
      let quipaStore: any = localStorage.getItem('quipa-store');
      if (quipaStore) {
        quipaStore = JSON.parse(quipaStore);
      }

      if (new Date().getTime() > exp * 1000) {
        delete user.user.token;
        delete user.user.refreshToken;
        localStorage.setItem('quipa-store', JSON.stringify({ ...quipaStore, user: user.user }));
        setUser(user);
        clearTeamSettingsAccessCache();
        localStorage.removeItem('appLoaded');
        localStorage.setItem(`logout-event-${accountId}`, 'logout' + Math.random());
        history.push('/login');
      }

      if (this.props.accountId) {
        const cachedTeamSettingsPlanAccess = readTeamSettingsPlanAccessCache(this.props.accountId);
        if (cachedTeamSettingsPlanAccess !== null) {
          this.setState({
            canAccessTeamSettings: cachedTeamSettingsPlanAccess,
            hasResolvedTeamSettingsAccess: true,
          });
        }

        try {
          const res = await quickbooksCheckIsConnected(this.props.accountId);
          if (res.connected === true && res.userinfo) {
            this.setState({ isConnected: true });
          }
        } catch (err) {
          this.setState({ isConnected: false });
        }
        try {
          const userPayload = {
            accountId: this.props.accountId,
            includeTeamData: true,
          };
          // Important for notebookprops
          const userRes = await getUser(userPayload);
          const accountRes = await this.getAccountWithStatusCheck(this.props.accountId);
          if (!accountRes) {
            return;
          }
          const parentAccountId = Number(accountRes?.parentAccountId);
          const canSeePricing = hasPricingAccess(accountRes?.canSeePricing);
          writeCachedPricingPermission(canSeePricing);
          if (
            accountRes &&
            !accountRes?.isOwner &&
            Number.isInteger(parentAccountId) &&
            parentAccountId > 0
          ) {
            localStorage.setItem('ownerAccountId', String(parentAccountId));
            localStorage.setItem('subaccountCanSeePricing', canSeePricing ? '1' : '0');
          } else {
            localStorage.removeItem('ownerAccountId');
            localStorage.removeItem('subaccountCanSeePricing');
          }
          const isTeamMemberAccount =
            !!accountRes &&
            !accountRes?.isOwner &&
            Number.isInteger(parentAccountId) &&
            parentAccountId > 0;
          this.setState((prev) => ({
            ...prev,
            // Important for notebookprops
            account: accountRes,
            accountUser: userRes ? { ...userRes, isOwner: accountRes?.isOwner || false } : null,
            accountIsAdmin: !!accountRes?.isAdmin,
            accountCanSeePricing: canSeePricing,
            remainingItemOrder: accountRes.remainingItemOrder || 0,
          }));
          const planInfoRes = await getCustomerPlanInfo(
            userRes?.teamContext?.owner?.accountId || this.props.accountId
          );
          const stripeRes = planInfoRes['main'];
          if (stripeRes && stripeRes.current_period_end) {
            const renewalDate = new Date(stripeRes.current_period_end * 1000);
            this.setState((prev) => {
              return {
                ...prev,
                stripeRenewalDate: moment(renewalDate).format('MMMM DD, YYYY') || undefined,
              };
            });
          }
          if (isTeamMemberAccount) {
            writeTeamSettingsPlanAccessCache(this.props.accountId, true);
            this.setState({ canAccessTeamSettings: true });
          } else if (stripeRes && !stripeRes.error) {
            const canAccessTeamSettings = hasTeamSettingsSeatsMetadata(stripeRes);
            writeTeamSettingsPlanAccessCache(this.props.accountId, canAccessTeamSettings);
            this.setState({ canAccessTeamSettings });
          }
        } catch (error) {
          console.error('Failed to resolve team settings access:', error);
        } finally {
          this.setState({ hasResolvedTeamSettingsAccess: true });
        }
      }
    }

    if (user && user.user && user.user.email) {
      const lastVisit = sessionStorage.getItem('lastVisit');

      if (!lastVisit) {
        const lastVisitTime = new Date();
        const lastVisitStr = JSON.stringify(lastVisitTime);
        sessionStorage.setItem('lastVisit', lastVisitStr);

        const updated = await handleUpdateUser({
          user: { email: user.user.email },
          email: user.user.email,
          lastVisitAt: lastVisitTime,
        });
        console.log(updated);
      }
    }

    if (!token || appLoaded) {
      return;
    }

    const res: any = await dispatch(ApplicationModule.initialAppSync(token));

    localStorage.setItem('accountId', res.account.id);
    const canSeePricing = hasPricingAccess(res?.account?.canSeePricing);
    writeCachedPricingPermission(canSeePricing);
    if (Number(res?.account?.ownerAccountId) > 0) {
      localStorage.setItem('ownerAccountId', String(res.account.ownerAccountId));
      localStorage.setItem('subaccountCanSeePricing', canSeePricing ? '1' : '0');
    } else {
      localStorage.removeItem('ownerAccountId');
      localStorage.removeItem('subaccountCanSeePricing');
    }
    localStorage.setItem('firstName', res.user.firstName);
    localStorage.setItem('lastName', res.user.lastName);
    localStorage.setItem('role', res.user.role);
    localStorage.setItem('avatarId', res.account.avatarId);
    localStorage.setItem('status', res.account.type);
    localStorage.setItem('createdAt', res.account.createdAt);
    this.setState((prev) => ({
      ...prev,
      accountCanSeePricing: canSeePricing,
    }));
    // const loaded = localStorage.getItem("status") !== "subscribed";

    // if (loaded) {
    //     return;
    // }

    if (res.error) {
      if (res.code === 401) {
        history.replace({
          pathname: '/login',
          state: {
            redirectAfterLogin: location.pathname,
            redirectAfterLoginSearch: location.search,
          },
        });
      } else {
        this.setState({
          loadingError: res.message,
        });
      }
    } else if (this.isUnsubscribedAccount(res.account)) {
      this.logoutCurrentUser();
      return;
    } else if (res.account.type === Application.PENDING_ACCOUNT_STATUS) {
      history.replace('/profile-setup');
      localStorage.setItem('appLoaded', String(true));
      return;
    } else if (!res.account.loginAllowed) {
      // credit card required
      history.replace({
        pathname: '/card',
        state: {
          redirectAfterLogin: location.pathname,
          redirectAfterLoginSearch: location.search,
        },
      });
    }
    localStorage.setItem('appLoaded', String(true));
  }

  public async componentDidUpdate(
    prevProps: Readonly<ApplicationProps & DispatchProp<AnyAction>>,
    prevState: Readonly<State>,
    snapshot?: any
  ): Promise<void> {
    if (prevState.refetchAccount !== this.state.refetchAccount) {
      const accountRes = await this.getAccountWithStatusCheck(this.props.accountId);
      if (!accountRes) return
      this.setState((prev) => ({ ...prev, account: accountRes }));
    }

    if (prevState.refetchUser !== this.state.refetchUser) {
      const userPayload = {
        accountId: this.props.accountId,
        includeTeamData: true,
      };
      const res = await getUser(userPayload);
      this.setState((prev) => ({ ...prev, accountUser: res }));
    }
  }

  public handleRefetchUser = () => {
    this.setState((prev) => ({ ...prev, refetchUser: !prev.refetchUser }));
  };

  public handleRefetchAccount = () => {
    this.setState((prev) => ({ ...prev, refetchAccount: !prev.refetchAccount }));
  };

  private setQuickBooksConnected = (isConnected: boolean) => {
    this.setState({ isConnected });
  };

  public handleRefetchAfterAddingPurchasedGoods = () => {
    this.setState((prev) => ({ ...prev, purchasedGoodsRefetch: !prev.purchasedGoodsRefetch }));
  };

  public handleShowBottomSheet = (val: boolean, client?: any) => {
    this.setState((prev) => ({
      ...prev,
      bottomSheetActive: val,
      prefilledClient: client || null,
      purchasedGoodsPrefill: !val ? null : prev.purchasedGoodsPrefill,
    }));
  };

  public handleOpenSinglePurchasedGoods = (val: number | null) => {
    this.setState((prev) => ({ ...prev, purchasedGoodsId: val }));
    this.handleShowBottomSheet(true);
  };

  public handlePreFill = (payload: any) => {
    this.setState((prev) => ({ ...prev, purchasedGoodsPrefill: payload }));
  };

  public handleResetAndCloseGoodsModal = () => {
    this.setState((prev) => ({ ...prev, purchasedGoodsId: null }));
    this.handleShowBottomSheet(false);
  };

  public handleDuplicateGoods = () => {
    this.setState((prev) => ({ ...prev, purchasedGoodsId: null }));
  };

  public handleResetAndClose = () => {
    this.handleResetAndCloseGoodsModal();
  };

  public handleRefetchAndClose = () => {
    this.handleResetAndCloseGoodsModal();
    this.handleRefetchAfterAddingPurchasedGoods();
  };

  public handleUpdateTeamMembers = (teamMember: any[]) => {
    this.setState((prev) => {
      let temp = { ...prev };
      if (
        prev.accountUser &&
        prev.accountUser.teamContext &&
        prev.accountUser.teamContext.teamMembers
      ) {
        temp = {
          ...prev,
          accountUser: {
            ...prev.accountUser,
            teamContext: {
              ...prev.accountUser.teamContext,
              teamMembers: teamMember,
            },
          },
        };
      }
      return temp;
    });
  };

  purchasedGoodsProps(): any {
    return {
      open: this.state.bottomSheetActive,
      prefilledClient: this.state.prefilledClient,
      onDuplicate: this.handleDuplicateGoods,
      onCancel: this.handleResetAndClose,
      onSave: this.handleRefetchAndClose,
      onAutoSave: this.handleRefetchAfterAddingPurchasedGoods,
      onDelete: this.handleRefetchAndClose,
      existingId: this.state.purchasedGoodsId,
      accountUser: this.state.accountUser,
      canAccessTeamSettings: this.state.canAccessTeamSettings,
      purchasedGoodsPrefill: this.state.purchasedGoodsPrefill,
      account: this.state.account || null,
    };
  }

  public onPageScroll = () => {
    const backToTop = (this.appRef?.current?.scrollTop || 0) > 0;
    if (this.state.backToTop !== backToTop) {
      this.setState({ backToTop });
    }
  };

  public pageScrollTo = (position: ScrollToPosition, val: number) => {
    if (this.appRef?.current) {
      this.appRef?.current?.scrollTo({
        [position]: val,
        behavior: 'smooth',
      });
    }
  };

  public render(): JSX.Element {
    const { fullSidebar } = this.state;
    const {
      appLoaded,
      userName,
      firstName,
      lastName,
      userRole,
      userEmail,
      accountId,
      avatarId,
      createdAt,
      logoId,
    } = this.props;
    const teamSettingsFallbackPath = '/projects';
    if (!appLoaded) {
    }

    return (
      <MainApplicationContainer uiSidebarFull={fullSidebar}>
        <HeaderContent>
          <QuipaLogoContainer onClick={this.goHome}>
            <QuipaLogo />
          </QuipaLogoContainer>
          <AppHeader
            logoId={logoId}
            avatarId={avatarId}
            createdAt={createdAt}
            userName={userName}
            firstName={firstName}
            lastName={lastName}
            userEmail={userEmail}
            accountId={accountId}
            onNavigateTo={this.handleNewNavigation}
            isAdmin={userRole === 'admin'}
            canAccessMyShop={this.state.accountCanSeePricing}
            canAccessTeamSettings={this.state.canAccessTeamSettings}
            onPlusClicked={this.handlePlusClicked}
            isOpen={this.state.isOpen}
            handlePlusClick={this.handlePlusClick}
            handleCloseModal={this.handleCloseBtnModal}
            handleTriggerModal={this.handleTriggerModal}
            handleShowBottomSheet={this.handleShowBottomSheet}
          />
        </HeaderContent>
        <SidebarContent
          id="sidebar"
          onMouseEnter={this.openSidebar}
          onMouseLeave={this.closeSidebar}
        >
          {fullSidebar && (
            <ClickOutside
              rootNodeId="sidebar"
              onOutsideClick={this.closeSidebar}
              ignoreIds="sidebar-control"
            />
          )}
          <Sidebar
            // expanded={fullSidebar}
            onNavigateTo={this.handleNewNavigation}
            activeValue={this.getActiveSidebarItem()}
            canAccessMyShop={this.state.accountCanSeePricing}
          />
        </SidebarContent>
        <MainContent ref={this.appRef} onScroll={() => this.onPageScroll()}>
          <Switch>
            {userRole === 'admin' ? (
              <Redirect exact from="/" to="/admin/all-users" />
            ) : (
              <Redirect exact from="/" to="/projects" />
            )}
            {/* tslint:disable-next-line: jsx-alignment */}
            {
              <Route
                path="/calculator"
                render={(props) => (
                  <CalculatorContainer
                    key={(props.location.state && props.location.state.tempKey) || 'calc'}
                    {...props}
                    handleCloseModal={this.handleCloseModal}
                    handleNotSavedOrder={this.handleNotSavedOrder}
                    handleNewNavigation={this.handleNewNavigation}
                    triggerModal={this.state.triggerModal}
                    cantLogOut={this.state.cantLogOut}
                    handleOpenSubscriptionPlanModal={this.handleOpenSubscriptionPlanModal}
                    isConnected={this.state.isConnected}
                    accountUser={this.state.accountUser}
                    canAccessTeamSettings={this.state.canAccessTeamSettings}
                  />
                )}
              />
            }

            <Route
              path="/projects"
              render={(props: RouteComponentProps) => (
                <ProjectsPage
                  isOpenModal={this.state.isOpenModal}
                  handleCloseModal={this.handleCloseModal}
                  handleOpenSubscriptionPlanModal={this.handleOpenSubscriptionPlanModal}
                  handleShowBottomSheet={this.handleShowBottomSheet}
                  accountUser={this.state.accountUser}
                  canAccessTeamSettings={this.state.canAccessTeamSettings}
                  hasResolvedTeamSettingsAccess={this.state.hasResolvedTeamSettingsAccess}
                  handleUpdateTeamMembers={this.handleUpdateTeamMembers}
                  {...props}
                />
              )}
            />

            {userRole === 'admin' ? (
              <Route
                path="/admin/all-users"
                render={(props: RouteComponentProps) => (
                  <AllUsersPage
                    isOpenModal={this.state.isOpenModal}
                    handleCloseModal={this.handleCloseModal}
                    handleOpenSubscriptionPlanModal={this.handleOpenSubscriptionPlanModal}
                    {...props}
                  />
                )}
              />
            ) : userRole === 'office_worker' ? (
              <Route
                path="/projects"
                render={(props: RouteComponentProps) => (
                  <ProjectsPage
                    isOpenModal={this.state.isOpenModal}
                    handleCloseModal={this.handleCloseModal}
                    handleOpenSubscriptionPlanModal={this.handleOpenSubscriptionPlanModal}
                    handleShowBottomSheet={this.handleShowBottomSheet}
                    accountUser={this.state.accountUser}
                    canAccessTeamSettings={this.state.canAccessTeamSettings}
                    hasResolvedTeamSettingsAccess={this.state.hasResolvedTeamSettingsAccess}
                    handleUpdateTeamMembers={this.handleUpdateTeamMembers}
                    {...props}
                  />
                )}
              />
            ) : null}
            <Route
              path="/overview"
              render={(
                props: RouteComponentProps & {
                  accountUser?: any;
                  canAccessTeamSettings?: boolean;
                  handleUpdateTeamMembers?(items: any[]): void;
                }
              ) => (
                <Overview
                  isOpenModal={this.state.isOpenModal}
                  handleCloseModal={this.handleCloseModal}
                  handleOpenSubscriptionPlanModal={this.handleOpenSubscriptionPlanModal}
                  isConnected={this.state.isConnected}
                  handleShowBottomSheet={this.handleShowBottomSheet}
                  handlePreFill={this.handlePreFill}
                  handleOpenSinglePurchasedGoods={this.handleOpenSinglePurchasedGoods}
                  purchasedGoodsRefetch={this.state.purchasedGoodsRefetch}
                  accountUser={this.state.accountUser}
                  canAccessTeamSettings={this.state.canAccessTeamSettings}
                  handleUpdateTeamMembers={this.handleUpdateTeamMembers}
                  backToTop={this.state.backToTop}
                  pageScrollTo={this.pageScrollTo}
                  {...props}
                />
              )}
            />

            <Route
              path={'/ordering-and-receiving'}
              render={(props: any) => {
                let orderingAndReceivingProps = {
                  ...props,
                  account: this.state.account || null,
                  accountUser: this.state.accountUser || null,
                  canAccessTeamSettings: this.state.canAccessTeamSettings,
                  purchasedGoodsRefetch: this.state.purchasedGoodsRefetch,
                  handleOpenSinglePurchasedGoods: this.handleOpenSinglePurchasedGoods,
                  backToTop: this.state.backToTop,
                  pageScrollTo: this.pageScrollTo,
                  canOpenPO: this.state.accountCanSeePricing
                };
                return <OrderingAndReceiving {...orderingAndReceivingProps} />;
              }}
            />

            <Route
              path="/clients"
              render={(
                props: RouteComponentProps & {
                  accountUser?: any;
                  canAccessTeamSettings?: boolean;
                  handleUpdateTeamMembers?(items: any[]): void;
                }
              ) => (
                <ClientsContainer
                  isOpenModal={this.state.isOpenModal}
                  handleCloseModal={this.handleCloseModal}
                  handleOpenSubscriptionPlanModal={this.handleOpenSubscriptionPlanModal}
                  isConnected={this.state.isConnected}
                  handleShowBottomSheet={this.handleShowBottomSheet}
                  handlePreFill={this.handlePreFill}
                  handleOpenSinglePurchasedGoods={this.handleOpenSinglePurchasedGoods}
                  purchasedGoodsRefetch={this.state.purchasedGoodsRefetch}
                  accountUser={this.state.accountUser}
                  canAccessTeamSettings={this.state.canAccessTeamSettings}
                  handleUpdateTeamMembers={this.handleUpdateTeamMembers}
                  backToTop={this.state.backToTop}
                  pageScrollTo={this.pageScrollTo}
                  {...props}
                />
              )}
            />
            <Route
              path="/myshop"
              render={(props: RouteComponentProps) => {
                const restrictedEstimateSettingsAccess =
                  !this.state.accountCanSeePricing &&
                  hasRestrictedEstimateSettingsMyShopAccess(props.location);

                return this.state.accountCanSeePricing || restrictedEstimateSettingsAccess ? (
                  <MyShopPage
                    isOpenModal={this.state.isOpenModal}
                    handleCloseModal={this.handleCloseModal}
                    restrictedEstimateSettingsAccess={restrictedEstimateSettingsAccess}
                    {...props}
                  />
                ) : (
                  <Redirect to="/overview" />
                );
              }}
            />
            <Route
              path="/suppliers"
              render={(props: RouteComponentProps) => (
                <SuppliersContainer
                  isOpenModal={this.state.isOpenModal}
                  handleCloseModal={this.handleCloseModal}
                  {...props}
                />
              )}
            />
            <Route
              path="/account_settings"
              render={(props: any) => {
                let accountProps = {
                  ...props,
                  account: this.state.account || null,
                  handleRefetchAccount: this.handleRefetchAccount,
                };
                return <AccountSettingsContainer {...accountProps} />;
              }}
            />
            <Route
              path="/team_settings"
              render={(props: RouteComponentProps & { handleRefetchUser?(): any }) =>
                this.state.canAccessTeamSettings ? (
                  <TeamSettingsPage handleRefetchUser={this.handleRefetchUser} {...props} />
                ) : !this.state.hasResolvedTeamSettingsAccess ? null : (
                  <Redirect to={teamSettingsFallbackPath} />
                )
              }
            />
            <Route
              path="/user_settings"
              render={(
                props: RouteComponentProps & {
                  accountUser?: any;
                  handleRefetchUser?(): void;
                  canAccessTeamSettings?: boolean;
                }
              ) => (
                <UserSettingsContainer
                  handleRefetchUser={this.handleRefetchUser}
                  accountUser={this.state.accountUser}
                  canAccessTeamSettings={this.state.canAccessTeamSettings}
                  {...props}
                />
              )}
            />
            {/*<Route path="/buy_more_orders" component={BuyMoreOrders}/>*/}
            <Route
              path="/quickbooks_connect"
              render={(props) => (
                <QuickBookConnect
                  accountId={this.props.accountId}
                  {...props}
                  isConnected={this.state.isConnected}
                  setConnected={this.setQuickBooksConnected}
                />
              )}
            />
          </Switch>
        </MainContent>
        <NotificationStackContainer />
        {this.state.isSupportModalOpen && (
          <SupportModal
            isModalOpen={this.state.isSupportModalOpen}
            onModalClose={this.toggleSupportModal}
          />
        )}
        <SubscriptionPlanModal
          uiActive={this.state.subscriptionPlanModalOpen}
          uiOnConfirm={() => {
            this.setState({
              subscriptionPlanModalOpen: false,
            });
            this.handleChangePlan();
          }}
          uiOnRequestClose={() => {
            this.setState({
              subscriptionPlanModalOpen: false,
            });
          }}
          uiCloseOnOutsideClick={true}
          uiCloseOnEsc={true}
          uiHeader="Order Limit Reached"
        >
          <span>
            {`The order limit has been reached for this month. New orders will be available on ${
              this.state.stripeRenewalDate || '(invalid date)'
            } or by upgrading your plan.`}
          </span>
        </SubscriptionPlanModal>
        <QuipaAssistantLauncher />

        <PurchasedGoods {...this.purchasedGoodsProps()} />

        <Toaster />
      </MainApplicationContainer>
    );
  }

  private handleOpenSubscriptionPlanModal = () => {
    this.setState((prev) => ({ ...prev, subscriptionPlanModalOpen: true }));
  };

  private handleTriggerModal = () => {
    this.setState((prev) => {
      return {
        ...prev,
        triggerModal: !prev.triggerModal,
      };
    });
  };

  private handleNotSavedOrder = (changes: boolean): void => {
    this.setState((prev) => {
      return {
        ...prev,
        cantLogOut: changes,
      };
    });
  };

  private handleNewNavigation = (val: string, saveCase?: boolean): void => {
    const { history } = this.props;
    if (val === 'myshop' && !this.state.accountCanSeePricing) {
      history.push('/overview');
      return;
    }
    if (val === 'team_settings' && !this.state.canAccessTeamSettings) {
      history.push('/projects');
      return;
    }
    switch (val) {
      case 'support': {
        this.setState({ isSupportModalOpen: true });
        return;
      }
      case 'logout': {
        if (this.state.cantLogOut && !saveCase) {
          return;
        }
        this.logoutCurrentUser();
        return;
      }
    }

    history.push(`/${val}`);

    if (val === 'overview' || val === 'clients' || val === 'myshop' || val === 'suppliers') {
      const event_name =
        val === 'overview'
          ? 'nav_overview'
          : val === 'clients'
            ? 'nav_clients'
            : val === 'myshop'
              ? 'nav_my_shop'
              : val === 'suppliers'
                ? 'nav_suppliers'
                : '';

      trackView(event_name, {
        page: location.pathname + location.search,
      });
    }
  };

  private handlePlusClicked = async (e: React.MouseEvent<HTMLLIElement>): Promise<void> => {
    const { history, accountId } = this.props;
    const remainingItemOrder = this.state.remainingItemOrder;
    if (e.currentTarget.textContent === 'Add Client') {
      history.push('/clients', { tempKey: Math.random() });
      this.setState({ isOpenModal: true });
    } else if (e.currentTarget.textContent === 'Add Supplier') {
      history.push('/suppliers', { tempKey: Math.random() });
      this.setState({ isOpenModal: true });
    } else if (e.currentTarget.id === 'add_drapery_order_btn') {
      const res = await this.getAccountWithStatusCheck(accountId);
      if (!res) {
        return;
      }
      const remainingItemOrder = res.remainingItemOrder || 0;
      this.setState((prev) => ({ ...prev, isOpen: false }));
      const can_create_order = await userCanCreateNewOrder(this.props.accountId);
      if (!can_create_order && remainingItemOrder === 0) {
        this.setState((prev) => ({ ...prev, subscriptionPlanModalOpen: true }));
        return;
      }
      history.push('/calculator?type=drapery-order', {
        tempKey: Math.random(),
        type: 'drapery-order',
        referrer:
          location.pathname.substring(1) !== 'calculator'
            ? location.pathname
            : history.location && history.location.state && history.location.state.referrer
              ? history.location.state.referrer
              : '/overview',
      });
    } else if (e.currentTarget.id === 'add_custom_order_btn') {
      this.setState((prev) => ({ ...prev, isOpen: false }));
      const can_create_order = await userCanCreateNewOrder(this.props.accountId);
      if (!can_create_order && remainingItemOrder === 0) {
        // this.setState((prev) => ({ ...prev, subscriptionPlanModalOpen: true }));
        // return;
      }
      history.push('/calculator?type=custom-order', {
        tempKey: Math.random(),
        type: 'custom-order',
        referrer:
          location.pathname.substring(1) !== 'calculator'
            ? location.pathname
            : history.location && history.location.state && history.location.state.referrer
              ? history.location.state.referrer
              : '/overview',
      });
    } else if (e.currentTarget.id === 'roman_shade_btn') {
      let draperyItem: any = localStorage.getItem('romanShadeItem');
      if (draperyItem) {
        draperyItem = JSON.parse(draperyItem);
      } else {
        draperyItem = {};
      }
      delete draperyItem.orderId;
      delete draperyItem.client;

      localStorage.setItem('romanShadeItem', JSON.stringify(draperyItem));

      this.setState((prev) => ({ ...prev, isOpen: false }));
      const can_create_order = await userCanCreateNewOrder(this.props.accountId);
      if (!can_create_order && remainingItemOrder === 0) {
        this.setState((prev) => ({ ...prev, subscriptionPlanModalOpen: true }));
        return;
      }
      history.push('/calculator?type=roman-shade-order', {
        tempKey: Math.random(),
        type: 'roman-shade-order',
        referrer:
          location.pathname.substring(1) !== 'calculator'
            ? location.pathname
            : history.location && history.location.state && history.location.state.referrer
              ? history.location.state.referrer
              : '/overview',
      });
    } else if (e.currentTarget.id === 'start_quipa_assistant_btn') {
      this.setState((prev) => ({ ...prev, isOpen: false }));
      openQuipaAssistant({ source: 'header_plus' });
    } else {
      if (!this.state.accountCanSeePricing) {
        this.setState({ isOpen: false });
        this.props.history.push('/overview');
        return;
      }
      history.push('/myshop', { tempKey: Math.random() });
      this.setState({ isOpenModal: true });
    }
    this.setState({
      isOpen: false,
    });
  };

  private async handleChangePlan(): Promise<void> {
    const { accountId } = this.props;
    const variant = checkGate(PRICING_TABLE_PLANS_GATE) ? 'new' : 'old';
    const session = await changeSubscriptionPlan(accountId, variant);

    window.location.href = session.url;
  }

  private handlePlusClick = () => {
    this.setState({ isOpen: !this.state.isOpen });
  };
  public handleCloseModal = () => {
    this.setState({ isOpenModal: false });
  };

  public handleCloseBtnModal = () => {
    this.setState({ isOpen: false });
  };
  private openSidebar = (): void => {
    if (!this.state.fullSidebar) {
      this.setState({ fullSidebar: true });
    }
  };

  private closeSidebar = (): void => {
    if (this.state.fullSidebar) {
      this.setState({ fullSidebar: false });
    }
  };

  private toggleSupportModal = (): void => {
    this.setState({
      isSupportModalOpen: !this.state.isSupportModalOpen,
    });
  };

  private goHome = (): void => {
    const { history } = this.props;
    history.push('/');
  };

  private isUnsubscribedAccount(account?: { type?: string | null }): boolean {
    return account?.type === Application.UNSUBSCRIBED_ACCOUNT_STATUS;
  }

  private async getAccountWithStatusCheck(accountId: number): Promise<Account | null> {
    const account = await getAccount(accountId);
    if (this.isUnsubscribedAccount(account)) {
      this.logoutCurrentUser();
      return null;
    }
    return account;
  }

  private logoutCurrentUser(): void {
    const { history, dispatch, accountId } = this.props;
    const { user, setUser } = this.context;

    dispatch(UserModule.logout());
    localStorage.setItem('isReloaded', 'false');
    localStorage.setItem('refreshed', 'false');
    localStorage.setItem(`logout-event-${accountId}`, 'logout' + Math.random());
    clearTeamSettingsAccessCache();

    const setOnlyEmail = {
      user: {
        email: this.props.userEmail,
      },
    };

    localStorage.setItem('quipa-store', JSON.stringify(setOnlyEmail));
    localStorage.removeItem('accountId');
    localStorage.removeItem('ownerAccountId');
    localStorage.removeItem('subaccountCanSeePricing');
    clearCachedPricingPermission();
    localStorage.removeItem('showModal');
    localStorage.removeItem('firstName');
    localStorage.removeItem('seed-other-item');
    localStorage.removeItem('seed-other-items');
    localStorage.removeItem('seed-otheritems');
    localStorage.removeItem('lastName');
    localStorage.removeItem('role');
    localStorage.removeItem('avatarId');
    localStorage.removeItem('logoId');
    localStorage.removeItem('status');
    localStorage.removeItem('isBeta');
    localStorage.removeItem('appLoaded');
    localStorage.removeItem('createdAt');
    sessionStorage.removeItem('lastVisit');
    document.cookie.split(';').forEach((item) => {
      const cookieName = item.trim().split('=')[0];
      if (cookieName.startsWith('crisp-client')) {
        const cookieDomain = '.quipa.com';
        const cookiePath = '/';

        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${cookiePath}; domain=${cookieDomain};`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${cookiePath};`;
      }
    });
    delete user.user.token;
    delete user.user.refreshToken;
    setUser(user);
    history.push('/login');
  }

  private getActiveSidebarItem(): string | undefined {
    const {
      location: { pathname },
    } = this.props;
    if (pathname === '/') {
      return 'clients';
    } else {
      return pathname.replace(/[^0-9a-zA-Z]/g, '');
    }
  }
}

function mapStateToProps(state: RootState, ownProps: ApplicationOwnProps): ApplicationProps {
  const user = localStorage.getItem('quipa-store');
  const token = user ? JSON.parse(user).token : '';
  const email = user ? JSON.parse(user).user.email : '';
  const firstName = localStorage.getItem('firstName') || '';
  const lastName = localStorage.getItem('lastName') || '';
  const avatarId = localStorage.getItem('avatarId');
  const createdAt = localStorage.getItem('createdAt') || '';
  const logoId = localStorage.getItem('logoId') || '';
  const role =
    localStorage.getItem('role') === 'admin' || localStorage.getItem('role') === 'office_worker'
      ? (localStorage.getItem('role') as 'admin' | 'office_worker')
      : 'office_worker';

  return {
    ...ownProps,
    appLoaded: state.application.loaded,
    token,
    userName: firstName,
    userRole: role,
    firstName,
    lastName,
    avatarId,
    accountId: parseFloat(localStorage.getItem('accountId') || '0'),
    userEmail: email,
    createdAt,
    logoId,
  };
}

export const ApplicationContainer = connect(mapStateToProps)(Application);
